import React, { useMemo } from 'react';
import { environment } from 'nsdi-volto-frontend/config/environments';

type FormData = {
  voyagerBaseUrl?: string;
  voyagerConfigId?: string;
  sidebarFacetFields?: Array<{ field: string; label?: string }>;
  sidebarFacetParams?: Array<{ key: string; value: string }>;
  sidebarFacetLimit?: number;
  sidebarFacetMinCount?: number;
};

type SidebarApiTestLinkWidgetProps = {
  id: string;
  title?: string;
  description?: string;
  formData?: FormData;
};

const DEFAULT_BASE_URL = environment.voyager.baseUrl;
const DEFAULT_CONFIG_ID = environment.voyager.configId;
const DEFAULT_FACET_FIELDS = [
  '{!ex=grp_NSDI}grp_NSDI',
  '{!ex=grp_NSDI_Sector}grp_NSDI_Sector',
  '{!ex=grp_NSDI_Organization}grp_NSDI_Organization',
  '{!ex=grp_NSDI_Agency}grp_NSDI_Agency',
  '{!ex=grp_NSDI_Program}grp_NSDI_Program',
  'tree_nsdi',
  '{!ex=fs_nsdi_classification}fs_nsdi_classification',
  '{!ex=format_category}format_category',
  'format',
  'format_type',
  'format_keyword',
  'geometry_type',
  'keywords',
  'properties',
  'author',
  'srs',
  'fileExtension',
  '{!ex=location}location',
];
const DEFAULT_FACET_PARAMS: Record<string, string> = {
  'tree_nsdi.facet.sort': 'index',
  'f.tree_nsdi.facet.sort': 'index',
};

const SidebarApiTestLinkWidget: React.FC<SidebarApiTestLinkWidgetProps> = ({
  id,
  title,
  description,
  formData,
}) => {
  const testUrl = useMemo(() => {
    const data = formData || {};
    const baseUrl = (data.voyagerBaseUrl || DEFAULT_BASE_URL).replace(
      /\/$/,
      '',
    );
    const configId = data.voyagerConfigId || DEFAULT_CONFIG_ID;
    const facetLimit = data.sidebarFacetLimit ?? 11;
    const facetMinCount = data.sidebarFacetMinCount ?? 1;

    // Build search URL
    const searchUrl = baseUrl.endsWith('/solr/v0/select')
      ? baseUrl
      : `${baseUrl}/solr/v0/select`;

    const params = new URLSearchParams();
    params.set('basemap', 'ESRI Street Map');
    params.set('disp', configId);
    params.set('view', 'card');
    params.set('filter', 'true');
    params.set('voyager.config.id', configId);
    params.set('rows', '0');
    params.set('facet', 'true');
    params.set('facet.mincount', String(facetMinCount));
    params.set('facet.limit', String(facetLimit));
    params.set('wt', 'json');

    // Add facet fields from configuration or use defaults
    const facetFields = data.sidebarFacetFields?.length
      ? data.sidebarFacetFields.map((f) => f.field)
      : DEFAULT_FACET_FIELDS;

    facetFields.forEach((field) => {
      if (field) {
        params.append('facet.field', field);
      }
    });

    // Add extra facet params (use custom or fall back to defaults)
    if (data.sidebarFacetParams?.length) {
      data.sidebarFacetParams.forEach(({ key, value }) => {
        if (key && value) {
          params.set(key, value);
        }
      });
    } else {
      // Use default facet params
      Object.entries(DEFAULT_FACET_PARAMS).forEach(([key, value]) => {
        params.set(key, value);
      });
    }

    return `${searchUrl}?${params.toString()}`;
  }, [formData]);

  return (
    <div className="field sidebar-api-test-link-widget">
      <div className="sidebar-api-test-link-widget-header">
        <label htmlFor={id}>{title || 'Sidebar API Test Link'}</label>
      </div>
      {description && <p className="help">{description}</p>}
      <div style={{ marginTop: '8px' }}>
        <a
          href={testUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-block',
            padding: '8px 16px',
            backgroundColor: '#007eb1',
            color: 'white',
            borderRadius: '4px',
            textDecoration: 'none',
            fontSize: '14px',
            fontWeight: 500,
          }}
        >
          Open Sidebar Facets API (Test)
        </a>
        <div
          style={{
            marginTop: '8px',
            padding: '8px',
            backgroundColor: '#f5f5f5',
            borderRadius: '4px',
            fontSize: '11px',
            fontFamily: 'monospace',
            wordBreak: 'break-all',
            maxHeight: '100px',
            overflow: 'auto',
          }}
        >
          {testUrl}
        </div>
      </div>
    </div>
  );
};

export default SidebarApiTestLinkWidget;
